import type { FC } from 'react';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { useForm, useField } from 'react-final-form';
import { InformationCircleLineSIcon } from '@alfalab/icons-glyph/InformationCircleLineSIcon';
import { Grid } from '@alfa-bank/site-grid-universal';
import { ImgV2 as Img } from '@alfa-bank/site-img-v2';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { BackgroundV2 as Background } from '@alfa-bank/site-background-v2';

import {
    renderPlaceholder,
    getPlaceholderText,
    getCategoryWordPlaceHolder,
    getCategoryWordTitle,
} from '../../helpers';

import type { TCashbackSelector, TOptionShape, TSelectedCategories } from './models';
import { validateCashbackCategories, getCurrentMonth } from './helpers';
import { renderCashbackItem } from './render-cashback-item';

export const CashbackSelector: FC<TCashbackSelector> = ({
    Button,
    Hr,
    Select,
    Text,
    Tooltip,
    cashbackList,
    config,
    getError,
    getTooltipProps,
    handleDesktopSelect,
    handleMobileSelect,
    settings,
    styles,
}) => {
    /** Состояние для хранения подтвержденных выборов категорий */
    const [selectedCategories, setSelectedCategories] = useState<TSelectedCategories>([]);
    /** Состояние для хранения временных выборов категорий (до нажатия кнопки "Применить") */
    const [tempSelectedCategories, setTempSelectedCategories] = useState<TSelectedCategories>([]);
    const { categories, groupId, offerId, requiredCount: initRequiredCount } = cashbackList;
    const formApi = useForm();

    const [isOpen, setIsOpen] = useState(false);
    const { hiddenCategories = [] } = settings;
    const visibleCategories = useMemo(
        () => categories.filter(item => !settings.hiddenCategories?.includes(item.id)),
        [categories, settings.hiddenCategories]
    );

    /** Фильтр скрытых категорий, которые также присутствуют в массиве categories */
    const filteredHiddenCategories = hiddenCategories.filter(hiddenId =>
        categories.some(category => category.id === hiddenId)
    );

    /** Вычисляем requiredCount с учетом найденных скрытых категорий */
    const requiredCount = initRequiredCount - filteredHiddenCategories.length;

    /**
     * При открытии шторки инициализируем временные выборы текущими подтвержденными категориями.
     * Это нужно чтобы при открытии шторки пользователь видел свои последние подтвержденные выборы
     */
    useEffect(() => {
        if (isOpen) {
            setTempSelectedCategories(selectedCategories);
        }
    }, [isOpen, selectedCategories]);

    /**
     * Обработчик нажатия кнопки "Применить".
     * Сохраняет временные выборы как подтвержденные и закрывает шторку
     */
    const handleApply = useCallback(() => {
        setSelectedCategories(tempSelectedCategories);
        setIsOpen(false);
    }, [tempSelectedCategories]);

    /**
     * Обработчик закрытия шторки через крестик.
     * Отменяет все временные изменения, возвращая состояние к последним подтвержденным выборам
     */
    const handleClose = useCallback(() => {
        setTempSelectedCategories(selectedCategories);
        setIsOpen(false);
    }, [selectedCategories]);

    /**
     * Обработчик нажатия кнопки "Сбросить".
     * Полностью очищает как временные, так и подтвержденные выборы категорий
     */
    const handleReset = useCallback(() => {
        setTempSelectedCategories([]);
        setSelectedCategories([]);
        setIsOpen(false);
    }, []);

    /**
     * Проверяет, достигнут ли лимит выбора категорий.
     * Использует временные выборы для проверки, пока шторка открыта
     */
    const isLimitReached = useMemo(
        () => tempSelectedCategories.length === requiredCount,
        [requiredCount, tempSelectedCategories.length]
    );

    /**
     * Формирует массив ID выбранных категорий.
     * Использует временные выборы, когда шторка открыта, и подтвержденные, когда закрыта.
     * Также добавляет скрытые категории к выбранным
     */
    const selectedCategoriesIds = useMemo(() => {
        const selectedItems = isOpen ? tempSelectedCategories : selectedCategories;

        if (selectedItems.length) {
            return [
                ...selectedItems.map(el => String(el.id)),
                ...filteredHiddenCategories.map(el => String(el)),
            ];
        }

        return [];
    }, [selectedCategories, tempSelectedCategories, filteredHiddenCategories, isOpen]);

    /**
     * Эффект для синхронизации выбранных категорий с состоянием формы.
     * При изменении выбранных категорий обновляет значение в форме,
     * при отсутствии выбранных категорий очищает значение в форме
     */
    useEffect(() => {
        if (!selectedCategoriesIds.length) {
            formApi.change('cashbackCategories', undefined);

            return;
        }

        const preparedData = {
            groupId,
            categories: [
                {
                    offerId,
                    ids: selectedCategoriesIds,
                },
            ],
        };
        formApi.change('cashbackCategories', preparedData);

        return () => {
            formApi.change('cashbackCategories', undefined);
        };
    }, [selectedCategoriesIds, formApi, groupId, offerId]);

    const {
        input: { value: selectedCashbackType },
    } = useField('selectedCashbackType');

    const options: TOptionShape[] = useMemo(
        () =>
            visibleCategories.map(item => {
                const isItemSelected = selectedCategoriesIds.includes(String(item.id));

                return {
                    key: String(item.id),
                    disabled: !item.isEditable || (isLimitReached && !isItemSelected),
                    showCheckMark: config.showCheckMark,
                    content: renderCashbackItem({
                        item,
                        isChecked: isItemSelected,
                        isLimitReached,
                        showPseudoCheckbox: config.showPseudoCheckbox,
                    }),
                    value: {
                        text: `${item.percentRate}% ${item.name}`,
                        isItemSelected,
                    },
                };
            }),
        [
            visibleCategories,
            config.showCheckMark,
            config.showPseudoCheckbox,
            isLimitReached,
            selectedCategoriesIds,
        ]
    );

    const placeholder = getPlaceholderText(requiredCount);
    const tooltipProps = getTooltipProps({ content: settings.hintText, title: settings.hintTitle });
    const title = `Выберите ${requiredCount} ${getCategoryWordTitle(
        requiredCount
    )} \nкэшбэка на ${getCurrentMonth()}`;

    return (
        <Background
            className={cn(styles.background, styles.external)}
            color="#ffffff"
            styler={{ borderRadius: 24, ...config.backgroundOffset }}
        >
            <Grid colAutoWidth justify="between" styler={config.titleOffset}>
                <div>
                    <Text styler={{ marginRight: 8 }} tag="span" view={config.titleView}>
                        {title}
                    </Text>
                    <Tooltip
                        contentClassName={
                            'customTooltip' in styles ? styles.customTooltip : undefined
                        }
                        fallbackPlacements={['bottom', 'top']}
                        position="top"
                        preventOverflow
                        targetClassName={cn(styles.target, styles.external)}
                        trigger="hover"
                        {...tooltipProps}
                    >
                        <InformationCircleLineSIcon
                            color="#6d7986"
                            height={config.titleIconSize}
                            width={config.titleIconSize}
                        />
                    </Tooltip>
                </div>
                <Img src={settings.iconSrc} {...config.titleImg} />
            </Grid>
            <Field<TOptionShape[]>
                name="cashbackCategories"
                render={({ input, meta }) => {
                    return (
                        <Select
                            allowUnselect
                            block
                            bottomSheetProps={{
                                contentClassName: cn(styles.wrapper, styles.external),
                                onClose: handleClose,
                                footerClassName: cn(styles.footer, styles.external),
                                actionButton: (
                                    <>
                                        <Hr marginBottom="0" marginTop="0" />
                                        <div className={cn(styles.buttonsWrapper, styles.external)}>
                                            <Grid
                                                className={cn(styles.buttons, styles.external)}
                                                collapse
                                                styler={{
                                                    margin: 16,
                                                }}
                                            >
                                                <Button
                                                    block
                                                    onClick={handleApply}
                                                    size="s"
                                                    view="primary"
                                                >
                                                    Применить
                                                </Button>
                                                <Button
                                                    block
                                                    onClick={handleReset}
                                                    size="s"
                                                    view="secondary"
                                                >
                                                    Сбросить
                                                </Button>
                                            </Grid>
                                        </div>
                                    </>
                                ),
                            }}
                            closeOnSelect={false}
                            dataTestId="dc-form-cashback-select"
                            error={getError({
                                meta,
                                error: `Выберите ${requiredCount} ${getCategoryWordPlaceHolder(
                                    requiredCount
                                )} кэшбэка`,
                                isDropdownOpen: isOpen,
                            })}
                            label={placeholder}
                            multiple={config.isMultipleSelect}
                            onBlur={input.onBlur}
                            onChange={payload => {
                                input.onChange(payload.selectedMultiple);

                                if (handleMobileSelect) {
                                    handleMobileSelect({
                                        setTempSelectedCategories,
                                        tempSelectedCategories,
                                        payload,
                                    });
                                } else if (handleDesktopSelect) {
                                    handleDesktopSelect({
                                        setSelectedCategories,
                                        payload,
                                    });
                                }

                                if (!payload.initiator) {
                                    setIsOpen(false);
                                }
                            }}
                            onFocus={input.onFocus}
                            onOpen={({ open }) => {
                                setIsOpen(!!open);
                            }}
                            open={isOpen}
                            optionClassName={cn(styles.option, styles.external)}
                            options={options}
                            optionsListProps={{
                                onClose: () => {},
                            }}
                            optionsSize="m"
                            placeholder={placeholder}
                            selected={selectedCategoriesIds}
                            size="m"
                            valueRenderer={() => renderPlaceholder(selectedCategories)}
                        />
                    );
                }}
                validate={() =>
                    selectedCashbackType === 'categories'
                        ? validateCashbackCategories(requiredCount, selectedCategories)
                        : undefined
                }
            />
        </Background>
    );
};
